/**
 * Stripe Service
 * 
 * Handles all Stripe-related API calls for subscription checkout and customer portal.
 * Uses direct axios calls to avoid interceptor issues.
 */

import axios from 'axios';
import { getEnvironmentConfig } from '../../../config/environment';

/**
 * Get API Base URL from environment configuration
 */
const getApiBaseUrl = () => {
  const config = getEnvironmentConfig();
  return config.api.baseURL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:80';
};

/**
 * API Endpoints
 */
const CHECKOUT_ENDPOINT = '/api/payment/create-checkout-session';
const PORTAL_ENDPOINT = '/api/payment/create-portal-session';

/**
 * Get auth token from localStorage
 */
const getAuthToken = () => {
  return localStorage.getItem('arbiwin_auth_token');
};

/**
 * Create axios headers with authentication
 */
const createHeaders = () => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

/**
 * Stripe Service Class
 */
class StripeService {
  /**
   * Create a Stripe checkout session
   * @param {string} lookupKey - The Stripe price lookup key
   * @returns {Promise<Object>} Checkout session result with redirect URL
   */
  static async createCheckoutSession(lookupKey) {
    console.log('🔍 StripeService: Creating checkout session with lookup_key:', lookupKey);
    
    try {
      const response = await axios.post(
        `${getApiBaseUrl()}${CHECKOUT_ENDPOINT}`,
        { lookup_key: lookupKey },
        { headers: createHeaders() }
      );

      console.log('🔍 StripeService: Response:', response.status, response.data);

      const checkoutUrl = response?.data?.url;

      if (!checkoutUrl) {
        throw new Error('No checkout URL returned from server');
      }

      return {
        success: true,
        url: checkoutUrl,
        sessionId: response.data?.session_id || null,
        error: null
      };
    } catch (error) {
      console.error('🔍 StripeService: Failed to create checkout session:', error);
      
      return {
        success: false,
        url: null,
        sessionId: null,
        error: this.handleStripeError(error)
      };
    }
  }

  /**
   * Create a Stripe customer portal session
   * @param {string} sessionId - The Stripe checkout session ID
   * @returns {Promise<Object>} Portal session result with redirect URL
   */
  static async createPortalSession(sessionId) {
    console.log('🔍 StripeService: Creating portal session with session_id:', sessionId);
    
    try {
      const response = await axios.post(
        `${getApiBaseUrl()}${PORTAL_ENDPOINT}`,
        { session_id: sessionId },
        { headers: createHeaders() }
      );

      console.log('🔍 StripeService: Portal response:', response.status, response.data);

      const portalUrl = response?.data?.url;

      if (!portalUrl) {
        throw new Error('No portal URL returned from server');
      }

      return {
        success: true,
        url: portalUrl,
        error: null
      };
    } catch (error) {
      console.error('🔍 StripeService: Failed to create portal session:', error);
      
      return {
        success: false,
        url: null,
        error: this.handleStripeError(error)
      };
    }
  }

  /**
   * Handle Stripe API errors
   * @param {Object} error - Error object from API call
   * @returns {Object} Formatted error object
   */
  static handleStripeError(error) {
    if (!error) {
      return {
        message: 'An unknown error occurred',
        code: 'UNKNOWN_ERROR',
        status: null
      };
    }

    // Extract error information
    const status = error.response?.status || null;
    const errorData = error.response?.data || {};
    const message = errorData.message || error.message || 'An error occurred';

    // Map common HTTP status codes to user-friendly messages
    const statusMessages = {
      400: 'Invalid request. Please check your information and try again.',
      401: 'Authentication required. Please log in and try again.',
      402: 'Payment required. Please check your subscription status.',
      403: 'Access denied. You do not have permission to perform this action.',
      404: 'Service not found. Please contact support.',
      429: 'Too many requests. Please wait a moment and try again.',
      500: 'Server error. Please try again later.',
      502: 'Service temporarily unavailable. Please try again later.',
      503: 'Service temporarily unavailable. Please try again later.'
    };

    return {
      message: statusMessages[status] || message,
      code: errorData.code || error.code || 'STRIPE_ERROR',
      status: status,
      details: errorData.details || null
    };
  }
}

export default StripeService;
